import {
  BOOK_PLAN_IDS,
  BOOK_PLANS,
  BookAttributesV1Schema,
  SPREAD_LAYOUT_IDS,
  type BookAttributesV1,
  type BookPlanId,
  type OptionalBookStep,
  type SpreadLayoutId,
} from '@kitup/book-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type JSX, type ReactNode } from 'react';
import { Link, useNavigationType, useParams, useSearchParams } from 'react-router';
import { booksApi, type BookDto, type UpdateBookInput } from '../api/books.js';
import {
  charactersApi,
  hasReadySelection,
  isApproved,
  locationsApi,
  thingsApi,
} from '../api/library.js';
import { CharactersStep } from '../books/CharactersStep.js';
import { LocationsStep } from '../books/LocationsStep.js';
import { StoryStep } from '../books/StoryStep.js';
import { ThingsStep } from '../books/ThingsStep.js';
import { IllustrationsStep } from '../books/IllustrationsStep.js';
import { CoverStep } from '../books/CoverStep.js';
import { ExportStep } from '../books/ExportStep.js';
import { VideoStep } from '../books/VideoStep.js';
import { NarrationStep } from '../books/NarrationStep.js';
import { deriveBuilderSteps } from '../books/steps.js';
import { BuilderStepNavigation } from '../books/BuilderStepNavigation.js';
import { useBookEvents } from '../books/useBookEvents.js';
import { useBookLooks } from '../books/useBookLooks.js';
import { StoryStyleField } from '../components/StoryStyleField.js';
import { formatListPrice, formatPrice } from '@kitup/brand';
// Use leaf modules: this package's index imports App, which imports this page.
// noinspection ES6PreferShortImport
import { useBrand } from '../brand/context.js';
// noinspection ES6PreferShortImport
import { useLegal } from '../brand/legal-context.js';
import { PaymentTerms } from '../books/PaymentTerms.js';
import { useI18n } from '../i18n/context.js';
import { planFeatures } from '../monetization/plans.js';
import { useAuth } from '../auth/context.js';
import { monetizationApi, type CheckoutResultDto } from '../api/monetization.js';
import { adminAccountsApi } from '../api/admin-accounts.js';

type SaveState = 'saved' | 'waiting' | 'saving' | 'error';

interface BookDraft {
  title: string;
  subtitle: string;
  styleId: string;
  targetAgeYears: number | null;
  occasion: string;
  attributes: BookAttributesV1;
}

export function BookBuilderPage(): JSX.Element {
  const { t } = useI18n();
  const { user } = useAuth();
  const { id = '', ownerId } = useParams();
  const adminMode = Boolean(ownerId);
  const billingExempt = user?.role === 'admin';
  const canAccess = !adminMode || user?.role === 'admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedStep = searchParams.get('step');
  const returnedPaymentId = searchParams.get('payment');
  const activeStep = requestedStep && BUILDER_STEP_IDS.has(requestedStep) ? requestedStep : 'main';
  // A step is a new screen, but only the query string changes, which `ScrollToTop`
  // deliberately leaves alone: on a phone "Next step" landed partway down the next step.
  // So a step change brings the step's card to the top when its start has been scrolled
  // past, and leaves the page alone when it is still on screen. Instantly and before paint,
  // so the new step is never drawn at the old position; back and forward are left to the
  // browser, as for pages.
  const navigationType = useNavigationType();
  const stepCard = useRef<HTMLDivElement | null>(null);
  const shownStep = useRef(activeStep);
  useLayoutEffect(() => {
    if (shownStep.current === activeStep) return;
    shownStep.current = activeStep;
    const card = stepCard.current;
    if (navigationType === 'POP' || !card) return;
    if (card.getBoundingClientRect().top < 0) card.scrollIntoView({ block: 'start' });
    // Focus follows: the button pressed is relabelled at the bottom of the new step, or gone,
    // and a keyboard or screen-reader user would be left there. The step's heading takes it
    // (Basics stays mounted but hidden, so the visible one); a step still loading its data
    // has no heading yet, and the card takes it instead.
    const target =
      [...card.querySelectorAll<HTMLElement>('h1')].find((h) => !h.closest('[hidden]')) ?? card;
    target.tabIndex = -1;
    target.focus({ preventScroll: true });
  }, [activeStep, navigationType]);
  useBookEvents(canAccess ? id : '');
  useBookLooks(id, canAccess);
  const queryClient = useQueryClient();
  const bookQuery = useQuery({
    queryKey: ['book', id],
    queryFn: () => booksApi.get(id),
    enabled: Boolean(id) && canAccess,
  });
  const bookCharacters = useQuery({
    queryKey: ['book-characters', id],
    queryFn: () => charactersApi.forBook(id),
    enabled: Boolean(id) && canAccess,
  });
  const bookThings = useQuery({
    queryKey: ['book-things', id],
    queryFn: () => thingsApi.forBook(id),
    enabled: Boolean(id) && canAccess,
  });
  const bookLocations = useQuery({
    queryKey: ['book-locations', id],
    queryFn: () => locationsApi.forBook(id),
    enabled: Boolean(id) && canAccess,
  });
  const [returnedPaymentWaiting, setReturnedPaymentWaiting] = useState(true);
  useEffect(() => {
    if (!returnedPaymentId) return;
    setReturnedPaymentWaiting(true);
    const timer = setTimeout(() => setReturnedPaymentWaiting(false), 60_000);
    return () => clearTimeout(timer);
  }, [returnedPaymentId]);
  const returnedPayment = useQuery({
    queryKey: ['payment', returnedPaymentId],
    queryFn: () => monetizationApi.payment(returnedPaymentId!),
    enabled: Boolean(returnedPaymentId) && canAccess && !billingExempt,
    refetchInterval: (current) => (current.state.data?.status === 'pending' ? 2_000 : false),
  });
  const [draft, setDraft] = useState<BookDraft | null>(null);
  const replacementLimit = useQuery({
    queryKey: ['admin-book-replacements', id],
    queryFn: () => adminAccountsApi.bookReplacementLimit(id),
    enabled: Boolean(id) && billingExempt,
  });
  const replacementLimitMutation = useMutation({
    mutationFn: (unlimited: boolean) => adminAccountsApi.setBookReplacementLimit(id, unlimited),
    onSuccess: (value) => queryClient.setQueryData(['admin-book-replacements', id], value),
  });
  const [saveState, setSaveState] = useState<SaveState>('saved');
  const lastSaved = useRef('');
  const confirmLayoutChange = useRef(false);
  const initializedBookId = useRef<string | null>(null);

  useEffect(() => {
    if (returnedPayment.data?.status !== 'succeeded') return;
    void queryClient.invalidateQueries({ queryKey: ['book', id] });
    void queryClient.invalidateQueries({ queryKey: ['books'] });
    void queryClient.invalidateQueries({ queryKey: ['book-illustrations', id] });
    void queryClient.invalidateQueries({ queryKey: ['book-cover', id] });
  }, [id, queryClient, returnedPayment.data?.status]);

  // "Your book is unlocked" is news once: when the book reads paid, the banner goes after a
  // few seconds instead of heading every step for the rest of the visit.
  const paidNow = Boolean(bookQuery.data?.paidAt);
  useEffect(() => {
    if (!returnedPaymentId || returnedPayment.data?.status !== 'succeeded' || !paidNow) return;
    const timer = setTimeout(
      () =>
        setSearchParams(
          (current) => {
            const next = new URLSearchParams(current);
            next.delete('payment');
            return next;
          },
          { replace: true },
        ),
      8_000,
    );
    return () => clearTimeout(timer);
  }, [returnedPaymentId, returnedPayment.data?.status, paidNow, setSearchParams]);

  useEffect(() => {
    if (!bookQuery.data || initializedBookId.current === bookQuery.data.id) return;
    const next = draftFrom(bookQuery.data);
    lastSaved.current = JSON.stringify(next);
    initializedBookId.current = bookQuery.data.id;
    setDraft(next);
    setSaveState('saved');
  }, [bookQuery.data, t]);

  useEffect(() => {
    if (!draft || !bookQuery.data) return;
    // У опубликованной книги поля заблокированы, но черновик мог остаться
    // с прошлого рендера: автосохранение здесь поймало бы только 409 (§3.16).
    if (bookQuery.data.publishedAt) return;
    const snapshot = JSON.stringify(draft);
    if (snapshot === lastSaved.current) return;
    setSaveState('waiting');

    const timeout = window.setTimeout(() => {
      const attributes = BookAttributesV1Schema.safeParse(draft.attributes);
      if (!attributes.success) {
        setSaveState('error');
        return;
      }
      setSaveState('saving');
      const currentConfirmation = confirmLayoutChange.current;
      void booksApi
        .update(id, coreUpdate(draft))
        .then(() => booksApi.updateAttributes(id, attributes.data, currentConfirmation))
        .then((updated) => {
          lastSaved.current = snapshot;
          confirmLayoutChange.current = false;
          queryClient.setQueryData(['book', id], updated);
          void queryClient.invalidateQueries({ queryKey: ['books'] });
          setSaveState('saved');
        })
        .catch(() => setSaveState('error'));
    }, 800);
    return () => window.clearTimeout(timeout);
  }, [bookQuery.data, draft, id, queryClient]);

  if (!canAccess) return <p role="alert">{t.admin.forbidden}</p>;
  if (bookQuery.isPending || !draft) return <p>{t.common.loading}</p>;
  if (bookQuery.isError) return <p role="alert">{t.common.unexpectedError}</p>;

  const book = bookQuery.data;
  const published = Boolean(book.publishedAt);
  const bookWithDraftAttributes = { ...book, attributes: draft.attributes };
  const linkedCharacters = bookCharacters.data?.items ?? [];
  const allCharactersApproved =
    linkedCharacters.length > 0 &&
    linkedCharacters.every((character) => isApproved(character, book.styleId));
  const hasReadyThing =
    bookThings.data?.items.some((thing) => hasReadySelection(thing, book.styleId)) ?? false;
  const linkedLocations = bookLocations.data?.items ?? [];
  const allLocationsReady =
    linkedLocations.length > 0 &&
    linkedLocations.every((location) => hasReadySelection(location, book.styleId));
  const steps = deriveBuilderSteps(
    bookWithDraftAttributes,
    t.builder.steps,
    allCharactersApproved,
    hasReadyThing,
    allLocationsReady,
  );
  const activeStepIndex = steps.findIndex((step) => step.id === activeStep);
  const activeStepData = activeStepIndex >= 0 ? steps[activeStepIndex] : undefined;
  const nextStep = activeStepIndex >= 0 ? steps[activeStepIndex + 1] : undefined;
  const optionalStep: OptionalBookStep | null =
    activeStep === 'things' || activeStep === 'locations' ? activeStep : null;
  const nextStepDisabled = activeStep === 'story' && activeStepData?.state !== 'ready';
  const preStoryReadOnly = book.sceneSummary.total > 0 && PRE_STORY_STEP_IDS.has(activeStep);
  const inspectablePreStoryStep = INSPECTABLE_PRE_STORY_STEP_IDS.has(activeStep);
  const disableWholeStep = preStoryReadOnly && !inspectablePreStoryStep;
  const openStep = (stepId: string): void =>
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (stepId === 'main') next.delete('step');
        else next.set('step', stepId);
        next.delete('payment');
        return next;
      },
      { replace: true },
    );

  const patchDraft = (patch: Partial<BookDraft>): void =>
    setDraft((current) => (current ? { ...current, ...patch } : current));
  const patchAttributes = (attributes: BookAttributesV1): void => patchDraft({ attributes });
  // `skippedSteps` records which optional steps the owner left with nothing in them, written
  // as they move on; nothing on screen reads it.
  const setOptionalStepSkipped = (step: OptionalBookStep, skipped: boolean): void => {
    const current = draft.attributes.workflow.skippedSteps.filter((item) => item !== step);
    if (skipped === draft.attributes.workflow.skippedSteps.includes(step)) return;
    patchAttributes({
      ...draft.attributes,
      workflow: {
        ...draft.attributes.workflow,
        skippedSteps: skipped ? [...current, step] : current,
      },
    });
  };

  const commitStyle = async (
    input: Pick<UpdateBookInput, 'styleId' | 'styleName'>,
  ): Promise<void> => {
    setSaveState('saving');
    try {
      const updated = await booksApi.update(id, input);
      setDraft((current) => {
        if (!current) return current;
        const next = { ...current, styleId: updated.styleId };
        lastSaved.current = JSON.stringify(next);
        return next;
      });
      queryClient.setQueryData(['book', id], updated);
      void queryClient.invalidateQueries({ queryKey: ['books'] });
      setSaveState('saved');
    } catch (cause) {
      setSaveState('error');
      throw cause;
    }
  };

  const selectLayout = (layout: SpreadLayoutId): void => {
    if (layout === draft.attributes.layout.default) return;
    const staleCount = book.layoutImpact[layout];
    if (staleCount > 0 && !window.confirm(t.builder.layoutConfirm(staleCount))) {
      return;
    }
    confirmLayoutChange.current = staleCount > 0;
    patchAttributes({
      ...draft.attributes,
      layout: { ...draft.attributes.layout, default: layout },
    });
  };

  const selectPlan = (plan: BookPlanId): void => {
    if (plan === book.plan || saveState !== 'saved') return;
    setSaveState('saving');
    void booksApi
      .update(id, { plan })
      .then((updated) => {
        const next = draftFrom(updated);
        lastSaved.current = JSON.stringify(next);
        setDraft(next);
        queryClient.setQueryData(['book', id], updated);
        void queryClient.invalidateQueries({ queryKey: ['books'] });
        setSaveState('saved');
      })
      .catch(() => setSaveState('error'));
  };

  return (
    // One readable column: the steps are forms and pictures, not a dashboard.
    <section className="mx-auto max-w-[900px]">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <Link to={adminMode ? '/admin/accounts' : '/app'} className="kitup-quiet-button">
          ← {t.builder.back}
        </Link>
        <div className="flex items-center gap-3">
          {published ? null : <SaveIndicator state={saveState} />}
          {/* Before a story there is nothing to read: the preview was a blank cover. */}
          {book.sceneSummary.total > 0 ? (
            <Link
              to={
                adminMode
                  ? `/admin/accounts/${ownerId}/books/${id}/preview`
                  : `/app/books/${id}/preview`
              }
              className="kitup-secondary-button"
            >
              {t.builder.preview}
            </Link>
          ) : null}
        </div>
      </div>

      {returnedPaymentId && !billingExempt ? (
        <div
          role="status"
          className="mb-5 rounded-2xl border border-[var(--kitup-color-border)] bg-[var(--kitup-color-surface-soft)] p-4 text-sm"
        >
          {returnedPayment.isPending || returnedPayment.data?.status === 'pending'
            ? t.monetization.payment.returnPending
            : returnedPayment.data?.status === 'succeeded'
              ? t.monetization.payment.returnSucceeded
              : returnedPayment.data?.status === 'canceled'
                ? t.monetization.payment.returnCanceled
                : t.monetization.payment.returnError}
        </div>
      ) : null}

      {adminMode ? (
        <p
          role="status"
          className="mb-5 rounded-2xl border border-violet-300 bg-violet-50 p-4 text-sm text-violet-900"
        >
          {t.admin.accounts.adminBookMode}
        </p>
      ) : null}

      {billingExempt ? (
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-violet-300 bg-violet-50 p-4 text-sm text-violet-900">
          <div>
            <p>{t.admin.accounts.billingExempt}</p>
            {replacementLimit.data ? (
              <p className="mt-1">
                {replacementLimit.data.unlimited
                  ? t.admin.accounts.replacementUnlimited
                  : t.admin.accounts.replacementLimit(
                      replacementLimit.data.used,
                      replacementLimit.data.included + replacementLimit.data.extra,
                    )}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {replacementLimit.data ? (
              <button
                type="button"
                className="kitup-secondary-button"
                disabled={replacementLimitMutation.isPending}
                onClick={() => replacementLimitMutation.mutate(!replacementLimit.data!.unlimited)}
              >
                {replacementLimit.data.unlimited
                  ? t.admin.accounts.restoreReplacementLimit
                  : t.admin.accounts.removeReplacementLimit}
              </button>
            ) : null}
            <Link
              to={`/admin/accounts/${ownerId ?? user.id}/books/${id}/manual`}
              className="kitup-primary-button"
            >
              {t.admin.accounts.manualEdit}
            </Link>
          </div>
        </div>
      ) : null}

      {/*
        Опубликованную книгу апи защищает от правок, но узнавать об этом
        из ошибки сохранения — плохой способ: причина и выход названы сразу.
      */}
      {/*
        The other steps say this inside their own card; Basics stays open for reading, so it
        needs the notice here. On Export the unpublish button is right there — no second one.
      */}
      {published && activeStep === 'main' ? (
        <div
          role="status"
          className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4"
        >
          <p className="max-w-prose text-sm text-amber-900">{t.publish.lockNotice}</p>
          <button
            type="button"
            className="kitup-secondary-button"
            onClick={() => setSearchParams({ step: 'export' }, { replace: true })}
          >
            {t.builder.goToStep(t.builder.steps.export)}
          </button>
        </div>
      ) : null}

      <div className="min-w-0">
        <BuilderStepNavigation steps={steps} activeStep={activeStep} onSelect={openStep} />

        <div ref={stepCard} className="kitup-card min-w-0 scroll-mt-4 p-5 sm:p-7">
          {published && activeStep !== 'main' && activeStep !== 'export' ? (
            <div role="status" className="grid justify-items-start gap-4">
              <p className="max-w-prose text-sm text-amber-900">{t.publish.lockNotice}</p>
              <button
                type="button"
                className="kitup-secondary-button"
                onClick={() => setSearchParams({ step: 'export' }, { replace: true })}
              >
                {t.builder.goToStep(t.builder.steps.export)}
              </button>
            </div>
          ) : null}
          {!published && preStoryReadOnly ? (
            <p
              id="pre-story-readonly-notice"
              role="status"
              className="mb-5 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
            >
              {t.builder.preStoryReadOnly}
            </p>
          ) : null}

          <fieldset
            className="contents"
            disabled={disableWholeStep}
            inert={disableWholeStep}
            aria-describedby={preStoryReadOnly ? 'pre-story-readonly-notice' : undefined}
          >
            {!published && activeStep === 'characters' ? (
              <CharactersStep book={book} readOnly={preStoryReadOnly} />
            ) : null}
            {!published && activeStep === 'things' ? (
              <ThingsStep book={bookWithDraftAttributes} readOnly={preStoryReadOnly} />
            ) : null}
            {!published && activeStep === 'locations' ? (
              <LocationsStep book={bookWithDraftAttributes} readOnly={preStoryReadOnly} />
            ) : null}
            {!published && activeStep === 'story' ? (
              <StoryStep
                book={book}
                billingExempt={billingExempt}
                // Unknown while the cast loads: approval waits for it rather than
                // being offered and refused.
                pendingCharacters={
                  bookCharacters.isSuccess
                    ? linkedCharacters
                        .filter((character) => !isApproved(character, book.styleId))
                        .map((character) => character.name)
                    : null
                }
              />
            ) : null}
            {!published && activeStep === 'illustrations' ? (
              allCharactersApproved ? (
                <IllustrationsStep
                  book={book}
                  billingExempt={billingExempt}
                  paymentConfirming={
                    // The server writes "succeeded" and the book's paidAt in one
                    // transaction, so this lasts until the book is refetched — or, for a
                    // payment the provider never confirms, a minute: then paying again
                    // is offered rather than a dialog that never comes back.
                    Boolean(returnedPaymentId) &&
                    !book.paidAt &&
                    returnedPaymentWaiting &&
                    (returnedPayment.isPending ||
                      returnedPayment.data?.status === 'pending' ||
                      returnedPayment.data?.status === 'succeeded')
                  }
                />
              ) : (
                <IllustrationsGate
                  total={linkedCharacters.length}
                  pending={
                    linkedCharacters.filter((character) => !isApproved(character, book.styleId))
                      .length
                  }
                />
              )
            ) : null}
            {!published && activeStep === 'cover' ? (
              book.paidAt || billingExempt ? (
                <CoverStep book={book} />
              ) : (
                <PaymentRequiredStep step="cover" stage={paymentStage(book)} />
              )
            ) : null}
            {!published && activeStep === 'video' ? (
              !book.paidAt && !billingExempt ? (
                <PaymentRequiredStep step="video" stage={paymentStage(book)} />
              ) : BOOK_PLANS[book.plan].video.enabled ? (
                <VideoStep
                  book={{ ...book, attributes: draft.attributes }}
                  billingExempt={billingExempt}
                />
              ) : (
                <PlanFeatureUnavailable step="video" />
              )
            ) : null}
            {!published && activeStep === 'narration' ? (
              !book.paidAt && !billingExempt ? (
                <PaymentRequiredStep step="narration" stage={paymentStage(book)} />
              ) : BOOK_PLANS[book.plan].narration ? (
                <NarrationStep bookId={book.id} />
              ) : (
                <PlanFeatureUnavailable step="narration" />
              )
            ) : null}
            {activeStep === 'export' ? (
              book.paidAt || billingExempt ? (
                <ExportStep book={book} />
              ) : (
                <PaymentRequiredStep step="export" stage={paymentStage(book)} />
              )
            ) : null}
            {OTHER_STEPS.includes(activeStep) ? (
              <p className="text-sm text-[var(--kitup-color-muted)]">{t.builder.soon}</p>
            ) : null}
            {/*
              Шаг «Основное» скрывается, а не размонтируется: у него отложенное
              автосохранение, и уход на соседний шаг не должен терять правку,
              которая ещё не успела уехать на сервер.
            */}
            <div hidden={activeStep !== 'main'}>
              <h1 className="text-2xl font-semibold">{t.builder.mainTitle}</h1>

              <div className="mt-7 grid items-start gap-5 sm:grid-cols-2">
                <div>
                  <TextField
                    label={t.builder.fields.title}
                    value={draft.title}
                    onChange={(title) => patchDraft({ title })}
                    disabled={published}
                    required
                  />
                  <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">
                    {t.books.bookTitleHint}
                  </p>
                </div>
                <div>
                  <NumberField
                    label={t.builder.fields.age}
                    value={draft.targetAgeYears}
                    min={1}
                    max={18}
                    disabled={published}
                    onChange={(targetAgeYears) => patchDraft({ targetAgeYears })}
                  />
                  {/* Each character has an age too; this one is the reader's, and it sets the words. */}
                  <p className="mt-1 text-xs text-[var(--kitup-color-muted)]">
                    {t.builder.fields.ageHint}
                  </p>
                </div>
              </div>

              <div className="mt-7">
                <StoryStyleField
                  label={t.builder.style}
                  style={book.style}
                  disabled={published || Boolean(book.styleLockedAt)}
                  onCommit={commitStyle}
                />
              </div>
              {book.styleLockedAt ? (
                <p className="mt-2 text-sm text-amber-800">{t.builder.styleLocked}</p>
              ) : null}

              <ChoiceSection title={t.builder.layout}>
                {SPREAD_LAYOUT_IDS.map((layout) => (
                  <button
                    key={layout}
                    type="button"
                    aria-pressed={draft.attributes.layout.default === layout}
                    aria-label={t.builder.layoutNames[layout]}
                    aria-describedby={`layout-hint-${layout}`}
                    className="kitup-choice-card"
                    disabled={published}
                    onClick={() => selectLayout(layout)}
                  >
                    <span className={`kitup-layout-preview kitup-layout-${layout}`}>
                      <i />
                      <b />
                    </span>
                    <span className="grid gap-0.5">
                      <span>{t.builder.layoutNames[layout]}</span>
                      <small
                        id={`layout-hint-${layout}`}
                        className="text-xs font-normal text-[var(--kitup-color-muted)]"
                      >
                        {t.builder.layoutHints[layout]}
                      </small>
                    </span>
                  </button>
                ))}
              </ChoiceSection>

              {draft.attributes.layout.default === 'split' ? (
                <label className="mt-4 flex items-center gap-3 text-sm">
                  <input
                    type="checkbox"
                    checked={draft.attributes.layout.alternateSides}
                    disabled={published}
                    onChange={(event) =>
                      patchAttributes({
                        ...draft.attributes,
                        layout: {
                          ...draft.attributes.layout,
                          alternateSides: event.target.checked,
                        },
                      })
                    }
                  />
                  {t.builder.alternateSides}
                </label>
              ) : null}

              {/* After the book's own fields: someone who came to name their book met a price list first. */}
              <PlanSelector
                bookId={book.id}
                selected={book.plan}
                promotionEnabled={!adminMode}
                billingExempt={billingExempt}
                paidPriceMinorUnits={book.paidPriceMinorUnits}
                disabled={
                  published ||
                  Boolean(book.paidAt) ||
                  book.hasStoryDraft ||
                  book.sceneSummary.total > 0 ||
                  saveState !== 'saved'
                }
                locked={
                  published ||
                  Boolean(book.paidAt) ||
                  book.hasStoryDraft ||
                  book.sceneSummary.total > 0
                }
                onSelect={selectPlan}
                onComplimentaryApplied={(checkout) => {
                  if (checkout.status !== 'succeeded' || !checkout.appliedAt) return;
                  queryClient.setQueryData<BookDto>(['book', id], (current) =>
                    current
                      ? {
                          ...current,
                          paidAt: checkout.appliedAt,
                          paidPriceMinorUnits: checkout.amountMinorUnits,
                        }
                      : current,
                  );
                  void queryClient.invalidateQueries({ queryKey: ['books'] });
                }}
              />
            </div>
          </fieldset>

          {!published && nextStep ? (
            <div className="mt-8 flex justify-end border-t border-[var(--kitup-color-border)] pt-5">
              <button
                type="button"
                // Past the free spread of an unpaid book, the next steps are locked and
                // paying is the move: "Next" steps back to a secondary button.
                className={
                  !book.paidAt && !billingExempt && PAID_STEP_IDS.has(nextStep.id)
                    ? 'kitup-secondary-button'
                    : 'kitup-primary-button'
                }
                disabled={nextStepDisabled}
                onClick={() => {
                  if (optionalStep) {
                    setOptionalStepSkipped(
                      optionalStep,
                      book.composition[optionalStep === 'things' ? 'things' : 'locations'] === 0,
                    );
                  }
                  openStep(nextStep.id);
                }}
              >
                {t.builder.nextStep(nextStep.label)} <span aria-hidden="true">→</span>
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function PlanSelector({
  bookId,
  selected,
  promotionEnabled,
  billingExempt,
  paidPriceMinorUnits,
  disabled,
  locked,
  onSelect,
  onComplimentaryApplied,
}: {
  readonly bookId: string;
  readonly selected: BookPlanId;
  readonly promotionEnabled: boolean;
  readonly billingExempt: boolean;
  /** What was paid, once paid: the summary says that rather than the list price. */
  readonly paidPriceMinorUnits: number | null;
  readonly disabled: boolean;
  /** The plan can no longer change (story, payment or publication), not just "saving". */
  readonly locked: boolean;
  onSelect(plan: BookPlanId): void;
  onComplimentaryApplied(checkout: CheckoutResultDto): void;
}): JSX.Element {
  const lockedOnArrival = useRef(locked);
  const { t, locale } = useI18n();
  const brand = useBrand();
  const [complimentaryPending, setComplimentaryPending] = useState(false);
  const controlsDisabled = disabled || complimentaryPending;
  const plan = BOOK_PLANS[selected];
  const price = billingExempt
    ? null
    : paidPriceMinorUnits !== null
      ? t.monetization.summaryPaid(formatPrice(paidPriceMinorUnits, brand, locale))
      : t.monetization.summaryUnpaid(formatPrice(brand.planPrices[selected], brand, locale));
  /*
   * Plan cards are for choosing. Once the plan can no longer change, three muted cards — or
   * even one — still read as an offer: what the book is becomes a plain summary, with what it
   * includes one click away. One section either way, so the code box below keeps its state
   * when a code unlocks the book and the cards turn into the summary.
   */
  return (
    <section className="mt-7 grid gap-2" aria-labelledby="book-plan-title">
      <h2 id="book-plan-title" className="font-semibold">
        {locked ? t.monetization.lockedTitle : t.monetization.chooseTitle}
      </h2>
      {locked ? (
        <>
          <p>{[t.monetization.plans[selected].name, price].filter(Boolean).join(' · ')}</p>
          {/* The length is the reason: the story was generated for it. */}
          <p className="text-sm text-[var(--kitup-color-muted)]">
            {t.monetization.locked(plan.sceneCount * 2 + 2)}
          </p>
          <details className="text-sm">
            <summary className="cursor-pointer font-medium">{t.monetization.included}</summary>
            <ul className="mt-2 grid gap-1">
              {planFeatures(plan, t.monetization.features).map((feature) => (
                <li
                  key={feature.text}
                  className="kitup-plan-feature"
                  data-included={String(feature.included)}
                >
                  {feature.text}
                </li>
              ))}
            </ul>
          </details>
        </>
      ) : (
        <>
          <p className="text-sm text-[var(--kitup-color-muted)]">{t.monetization.chooseSubtitle}</p>
          <div className="kitup-plan-comparison-scroll mt-1">
            <div className="kitup-plan-comparison">
              {BOOK_PLAN_IDS.map((id) => {
                const plan = BOOK_PLANS[id];
                const features = planFeatures(plan, t.monetization.features);
                const active = selected === id;
                return (
                  <div
                    key={id}
                    className="kitup-plan-option"
                    data-active={active ? 'true' : 'false'}
                    style={{ gridRow: `span ${features.length + 3}` }}
                  >
                    <button
                      type="button"
                      aria-pressed={active}
                      className="kitup-plan-option-choice"
                      disabled={controlsDisabled}
                      onClick={() => onSelect(id)}
                    >
                      <span className="kitup-plan-flag">
                        {id === 'live_book' ? (
                          <span className="kitup-plan-badge">{t.monetization.popular}</span>
                        ) : null}
                      </span>
                      <span className="kitup-plan-option-title">
                        <span className="kitup-plan-radio" aria-hidden="true" />
                        <span className="kitup-plan-name">{t.monetization.plans[id].name}</span>
                      </span>
                      <span className="kitup-plan-option-facts">
                        {billingExempt ? null : (
                          <span className="kitup-plan-price">
                            {formatListPrice(brand.planPrices[id], brand, locale)}
                          </span>
                        )}
                        <span className="text-[var(--kitup-color-muted)]">
                          {t.monetization.pagesShort(plan.sceneCount * 2 + 2)}
                        </span>
                      </span>
                    </button>
                    <ul
                      className="kitup-plan-option-features"
                      style={{ gridRow: `span ${features.length}` }}
                    >
                      {features.map((feature) => (
                        <li
                          key={feature.text}
                          className="kitup-plan-feature"
                          data-included={String(feature.included)}
                        >
                          {feature.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
      {/*
        A code changes the price of a plan that can still be chosen; on a book that was already
        locked when the page opened it has no use. One that a code unlocks here stays, so the
        confirmation is seen.
      */}
      {promotionEnabled && !billingExempt && !lockedOnArrival.current ? (
        <PlanPromotion
          bookId={bookId}
          selected={selected}
          disabled={controlsDisabled}
          onComplimentaryApplied={onComplimentaryApplied}
          onComplimentaryPendingChange={setComplimentaryPending}
        />
      ) : null}
    </section>
  );
}

function PlanPromotion({
  bookId,
  selected,
  disabled,
  onComplimentaryApplied,
  onComplimentaryPendingChange,
}: {
  readonly bookId: string;
  readonly selected: BookPlanId;
  readonly disabled: boolean;
  onComplimentaryApplied(checkout: CheckoutResultDto): void;
  onComplimentaryPendingChange(pending: boolean): void;
}): JSX.Element {
  const { t, locale } = useI18n();
  const brand = useBrand();
  const [searchParams, setSearchParams] = useSearchParams();
  const [promoCode, setPromoCode] = useState(() => searchParams.get('promo')?.toUpperCase() ?? '');
  const [quotedPlan, setQuotedPlan] = useState<BookPlanId | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const legal = useLegal();
  const payment = useMutation({
    mutationFn: (code: string) => monetizationApi.pay(bookId, legal.documentVersionId, code),
    onMutate: () => onComplimentaryPendingChange(true),
    onSuccess: onComplimentaryApplied,
    onSettled: () => onComplimentaryPendingChange(false),
  });
  const promotion = useMutation({
    mutationFn: (input: { readonly code: string; readonly plan: BookPlanId }) =>
      monetizationApi.quotePromotion(bookId, input.code),
    onSuccess: (quote, input) => {
      setQuotedPlan(input.plan);
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set('promo', quote.code);
          return next;
        },
        { replace: true },
      );
    },
  });
  const pending = promotion.isPending || payment.isPending;
  const currentQuote = quotedPlan === selected ? promotion.data : undefined;

  return (
    <div className="mt-5 grid max-w-lg gap-2 rounded-xl border border-[var(--kitup-color-border)] p-4">
      <label className="text-sm font-medium" htmlFor="plan-promo-code">
        {t.monetization.payment.promoLabel}
      </label>
      <div className="flex gap-2">
        <input
          id="plan-promo-code"
          className="min-w-0 flex-1"
          value={promoCode}
          placeholder={t.monetization.payment.promoPlaceholder}
          disabled={disabled || pending}
          onChange={(event) => {
            setPromoCode(event.target.value.toUpperCase());
            setQuotedPlan(null);
            promotion.reset();
            payment.reset();
          }}
        />
        <button
          type="button"
          className="kitup-secondary-button"
          disabled={disabled || !promoCode.trim() || pending}
          onClick={() => promotion.mutate({ code: promoCode, plan: selected })}
        >
          {t.monetization.payment.promoApply}
        </button>
      </div>
      {currentQuote ? (
        <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" role="status">
          <p>
            {currentQuote.fixedDiscountMinorUnits != null
              ? t.monetization.payment.promoAmountApplied(
                  formatPrice(currentQuote.discountAmountMinorUnits, brand, locale),
                )
              : t.monetization.payment.promoApplied(currentQuote.discountPercent)}
          </p>
          <p className="mt-1">
            <s>{formatPrice(currentQuote.listAmountMinorUnits, brand, locale)}</s>{' '}
            <strong>{formatPrice(currentQuote.amountMinorUnits, brand, locale)}</strong>
          </p>
          {!payment.isSuccess ? (
            <p className="mt-1">{t.monetization.payment.promoPlanHint}</p>
          ) : null}
          {payment.isSuccess ? (
            <p className="mt-1">{t.monetization.payment.promoUnlocked}</p>
          ) : null}
        </div>
      ) : null}
      {currentQuote?.amountMinorUnits === 0 && !payment.isSuccess ? (
        <>
          <PaymentTerms
            checked={termsAccepted}
            disabled={disabled || pending}
            onChange={setTermsAccepted}
          />
          <button
            type="button"
            className="kitup-primary-button mt-1 justify-self-start"
            disabled={disabled || pending || !termsAccepted}
            onClick={() => payment.mutate(currentQuote.code)}
          >
            {payment.isPending
              ? t.monetization.payment.freeProcessing
              : t.monetization.payment.freeAction}
          </button>
        </>
      ) : null}
      {promotion.isError ? (
        <p role="alert" className="text-sm text-red-700">
          {t.monetization.payment.promoInvalid}
        </p>
      ) : null}
      {payment.isError ? (
        <p role="alert" className="text-sm text-red-700">
          {t.monetization.payment.error}
        </p>
      ) : null}
    </div>
  );
}

/** Шаги, чей интерфейс появится в следующих эпиках. */
const BUILDER_STEP_IDS = new Set([
  'main',
  'characters',
  'things',
  'locations',
  'story',
  'illustrations',
  'cover',
  'video',
  'narration',
  'export',
]);
const OTHER_STEPS: readonly string[] = [];
const PRE_STORY_STEP_IDS = new Set(['main', 'characters', 'things', 'locations']);
const INSPECTABLE_PRE_STORY_STEP_IDS = new Set(['characters', 'things', 'locations']);

/**
 * Гейт шага «Иллюстрации» (E7-T2 DoD): пока герои книги не утверждены в её
 * стиле, генерировать сцены нельзя — и пользователю объясняется почему,
 * а не показывается молчаливая ошибка.
 *
 * Вещей и локаций гейт не касается: они опциональны (§3.6, §3.7).
 */
function IllustrationsGate({ total, pending }: { total: number; pending: number }): JSX.Element {
  const { t } = useI18n();
  if (total === 0) {
    return (
      <p role="status" className="text-sm text-amber-800">
        {t.builder.locked.noCharacters}
      </p>
    );
  }
  if (pending === 0) {
    return <p className="text-sm text-[var(--kitup-color-muted)]">{t.builder.soon}</p>;
  }
  return (
    <div role="status" className="grid gap-2">
      <p className="text-sm text-amber-800">{t.builder.locked.illustrations}</p>
      <p className="text-sm text-[var(--kitup-color-muted)]">
        {t.builder.locked.illustrationsCount(pending, total)}
      </p>
    </div>
  );
}

/**
 * A step the plan includes but payment has not opened. It used to be one line with no heading
 * — the same on four steps — and a "Preview the first spread" link that only went back to
 * Illustrations, where the preview had already been seen.
 */
function PaymentRequiredStep({
  step,
  stage,
}: {
  readonly step: 'cover' | 'video' | 'narration' | 'export';
  readonly stage: PaymentStage;
}): JSX.Element {
  const { t } = useI18n();
  const target = stage === 'story' ? 'story' : 'illustrations';
  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t.builder.steps[step]}</h1>
      <p className="max-w-prose text-sm">{t.monetization.payment.lockedStep[step]}</p>
      <Link to={`?step=${target}`} className="kitup-primary-button justify-self-start">
        {stage === 'story'
          ? t.builder.goToStep(t.builder.steps.story)
          : stage === 'pay'
            ? t.monetization.payment.goToPay
            : t.monetization.payment.goToPreview}
      </Link>
    </div>
  );
}

/** Where an unpaid book stands on the way to payment: which step unlocks the rest. */
type PaymentStage = 'story' | 'preview' | 'pay';

function paymentStage(book: BookDto): PaymentStage {
  if (book.sceneSummary.total === 0) return 'story';
  return book.sceneSummary.generated > 0 ? 'pay' : 'preview';
}

/**
 * Steps an unpaid book reaches only locked — the same four the step switch above renders as
 * PaymentRequiredStep (the "Next" button reads this list; keep the two together).
 */
const PAID_STEP_IDS: ReadonlySet<string> = new Set(['cover', 'video', 'narration', 'export']);

/**
 * A step the book's plan doesn't include. It was one line in a box with no heading — the
 * only step without its own title — and said nothing of what the step would have been.
 */
function PlanFeatureUnavailable({ step }: { step: 'video' | 'narration' }): JSX.Element {
  const { t } = useI18n();
  return (
    <div className="grid gap-3">
      <h1 className="text-2xl font-semibold">{t.builder.steps[step]}</h1>
      <p className="text-sm text-[var(--kitup-color-muted)]">
        {t.monetization.unavailableStep[step]}
      </p>
      <p className="text-sm">{t.monetization.unavailable}</p>
    </div>
  );
}

function draftFrom(book: BookDto): BookDraft {
  return {
    title: book.title,
    subtitle: book.subtitle ?? '',
    styleId: book.styleId,
    targetAgeYears: book.targetAgeYears,
    occasion: book.occasion ?? '',
    attributes: structuredClone(book.attributes),
  };
}

function coreUpdate(draft: BookDraft): UpdateBookInput {
  return {
    title: draft.title.trim(),
    subtitle: draft.subtitle.trim() || null,
    targetAgeYears: draft.targetAgeYears,
    occasion: draft.occasion.trim() || null,
  };
}

function SaveIndicator({ state }: { state: SaveState }): JSX.Element {
  const { t } = useI18n();
  return (
    <span role="status" className={`kitup-save-state kitup-save-state-${state}`}>
      {t.builder.save[state]}
    </span>
  );
}

function ChoiceSection({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  return (
    <fieldset className="mt-7">
      <legend className="mb-3 font-semibold">{title}</legend>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
    </fieldset>
  );
}

function TextField({
  label,
  value,
  onChange,
  required = false,
  disabled = false,
}: {
  label: string;
  value: string;
  onChange(value: string): void;
  required?: boolean;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="grid gap-1.5 text-sm font-medium">
      {label}
      <input
        className="kitup-input"
        value={value}
        required={required}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
  disabled = false,
}: {
  label: string;
  value: number | null;
  min: number;
  max: number;
  onChange(value: number | null): void;
  disabled?: boolean;
}): JSX.Element {
  return (
    <label className="grid gap-1.5 text-sm font-medium">
      {label}
      <input
        className="kitup-input"
        type="number"
        min={min}
        max={max}
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
      />
    </label>
  );
}
